// Split from djValueAdd.ts (I2). Shared local runtime helpers kept per-file for isolation.
import { SeqCounter } from "@djsem/shared";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 42 — Transition Assistant
// ============================================================================

/** A mixing technique that can be used to transition between two tracks. */
export type DJSEMTransitionStyle =
  | "long_blend" | "short_blend" | "phrase_mix" | "loop_blend" | "bass_swap"
  | "vocal_swap" | "echo_out" | "filter_sweep" | "breakdown_mix" | "drop_swap"
  | "hard_cut" | "ambient_bridge" | "manual_custom";

/** The kind of compatibility problem detected between two tracks in a transition. */
export type DJSEMTransitionIssueType =
  | "bpm_gap" | "key_clash" | "phrase_mismatch" | "vocal_clash"
  | "energy_drop" | "metadata_missing" | "no_safe_mix_window" | "low_confidence";

/** A candidate time window within a track suitable for mixing in, mixing out, or bridging. */
export interface DJSEMMixWindow {
  id: string;
  trackId: string;
  windowType: "mix_in" | "mix_out" | "bridge";
  startTimeMs?: number; endTimeMs?: number;
  startBar?: number; endBar?: number;
  phraseLabel?: string;
  confidence: number;
  reasons: string[];
  warnings: string[];
}

/** A single detected compatibility problem between a track pair. */
export interface DJSEMTransitionIssue {
  id: string;
  issueType: DJSEMTransitionIssueType;
  severity: "info" | "warning" | "high" | "critical";
  explanation: string;
  evidence: string[];
  confidence: number;
}

/** A suggested transition technique between two tracks, with an estimated difficulty and confidence. */
export interface DJSEMTransitionSuggestion {
  id: string;
  fromTrackId: string;
  toTrackId: string;
  style: DJSEMTransitionStyle;
  estimatedDifficulty: "easy" | "medium" | "hard" | "unknown";
  confidence: number;
  explanation: string;
  warnings: string[];
}

/** The full result of analysing a track pair: compatibility score, mix windows, suggestions, and issues. */
export interface DJSEMTransitionAssistantReport {
  id: string;
  fromTrackId: string;
  toTrackId: string;
  compatibilityScore: number;
  compatibilityConfidence: number;
  mixWindows: DJSEMMixWindow[];
  suggestions: DJSEMTransitionSuggestion[];
  issues: DJSEMTransitionIssue[];
  createdAt: string;
  warnings: string[];
}

/** Minimal metadata for the two tracks being analysed for a transition. */
export interface TransitionPairInput {
  fromTrack: { id: string; bpm?: number; key?: string; energy?: number; hasVocals?: boolean; durationMs?: number; };
  toTrack: { id: string; bpm?: number; key?: string; energy?: number; hasVocals?: boolean; durationMs?: number; };
}

/** Analyses track pairs for BPM/key/vocal/energy transition risk and suggests mixing approaches. */
export class DJSEMTransitionRiskAnalyzer {
  /**
   * Detects a large BPM gap (or missing BPM metadata) between the two tracks.
   * @param input - the from/to track pair.
   * @returns a list of issues (empty if the BPM gap is small and both BPMs are known).
   */
  detectBpmGap(input: TransitionPairInput): DJSEMTransitionIssue[] {
    if (!input.fromTrack.bpm || !input.toTrack.bpm) return [{ id: `ti_${_seq.next()}`, issueType: "metadata_missing", severity: "warning", explanation: "BPM data missing for one or both tracks", evidence: [], confidence: 1 }];
    const diff = Math.abs(input.fromTrack.bpm - input.toTrack.bpm);
    if (diff > 8) return [{ id: `ti_${_seq.next()}`, issueType: "bpm_gap", severity: diff > 15 ? "high" : "warning", explanation: `BPM gap: ${input.fromTrack.bpm} → ${input.toTrack.bpm} (${diff} BPM)`, evidence: [`from: ${input.fromTrack.bpm}`, `to: ${input.toTrack.bpm}`], confidence: 0.9 }];
    return [];
  }

  /**
   * Detects a musical key mismatch (or missing key metadata) between the two tracks.
   * @param input - the from/to track pair.
   * @returns a list of issues (empty if the keys match).
   */
  detectKeyClash(input: TransitionPairInput): DJSEMTransitionIssue[] {
    if (!input.fromTrack.key || !input.toTrack.key) return [{ id: `ti_${_seq.next()}`, issueType: "metadata_missing", severity: "info", explanation: "Key data missing", evidence: [], confidence: 1 }];
    // Simplified: same key = compatible, different = warn
    if (input.fromTrack.key !== input.toTrack.key) return [{ id: `ti_${_seq.next()}`, issueType: "key_clash", severity: "warning", explanation: `Key change: ${input.fromTrack.key} → ${input.toTrack.key}`, evidence: [`from: ${input.fromTrack.key}`, `to: ${input.toTrack.key}`], confidence: 0.6 }];
    return [];
  }

  /**
   * Detects whether both tracks have vocals, which risks a vocal clash during a blend.
   * @param input - the from/to track pair.
   * @returns a list of issues (empty if vocal presence is unknown for either track or only one has vocals).
   */
  detectVocalClash(input: TransitionPairInput): DJSEMTransitionIssue[] {
    if (input.fromTrack.hasVocals === undefined || input.toTrack.hasVocals === undefined) return [];
    if (input.fromTrack.hasVocals && input.toTrack.hasVocals) return [{ id: `ti_${_seq.next()}`, issueType: "vocal_clash", severity: "warning", explanation: "Both tracks have vocals — potential clash during blend", evidence: [], confidence: 0.7 }];
    return [];
  }

  /**
   * Detects a large energy drop from the outgoing track to the incoming track.
   * @param input - the from/to track pair.
   * @returns a list of issues (empty if energy is unknown for either track or the drop is small).
   */
  detectEnergyDrop(input: TransitionPairInput): DJSEMTransitionIssue[] {
    if (input.fromTrack.energy === undefined || input.toTrack.energy === undefined) return [];
    const drop = input.fromTrack.energy - input.toTrack.energy;
    if (drop > 0.4) return [{ id: `ti_${_seq.next()}`, issueType: "energy_drop", severity: "warning", explanation: `Large energy drop: ${input.fromTrack.energy.toFixed(1)} → ${input.toTrack.energy.toFixed(1)}`, evidence: [], confidence: 0.8 }];
    return [];
  }

  /**
   * Runs all detectors against a track pair and produces a combined compatibility report,
   * including a suggested transition style when compatibility is reasonable.
   * @param input - the from/to track pair.
   * @returns the full transition assistant report with score, issues, and suggestions.
   */
  analysePair(input: TransitionPairInput): DJSEMTransitionAssistantReport {
    const issues = [...this.detectBpmGap(input), ...this.detectKeyClash(input), ...this.detectVocalClash(input), ...this.detectEnergyDrop(input)];
    const highIssues = issues.filter(i => i.severity === "high" || i.severity === "critical").length;
    const score = Math.max(0, 1 - highIssues * 0.3 - issues.filter(i => i.severity === "warning").length * 0.1);
    const suggestions: DJSEMTransitionSuggestion[] = [];
    if (score > 0.3) {
      const hasBpmGap = issues.some(i => i.issueType === "bpm_gap");
      suggestions.push({
        id: `ts_${_seq.next()}`, fromTrackId: input.fromTrack.id, toTrackId: input.toTrack.id,
        style: hasBpmGap ? "hard_cut" : "long_blend",
        estimatedDifficulty: hasBpmGap ? "hard" : score > 0.7 ? "easy" : "medium",
        confidence: score, explanation: hasBpmGap ? "BPM gap suggests hard cut or echo out" : "Compatible pair — try a long blend",
        warnings: [],
      });
    }
    return {
      id: `tar_${_seq.next()}`, fromTrackId: input.fromTrack.id, toTrackId: input.toTrack.id,
      compatibilityScore: score, compatibilityConfidence: 0.7,
      mixWindows: [], suggestions, issues, createdAt: AT, warnings: [],
    };
  }
}
